import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { DEFAULT_SPECIALS_CARD_IMG } from 'shared/constants';
import { replaceAWSSourceWithImgix } from 'shared/helpers/imgix';
import { formatCurrency, pluralize } from 'shared/helpers/utils';
import { GamificationThresholdType } from 'src/types';
import useCart from 'src/hooks/use-cart';
import { ProgressCircle } from 'src/components/progress-circle';
import { DiscountTag } from 'src/assets/discount-tag';

type SpecialOfferCardSpecial = {
  _id?: string | null;
  name?: string | null;
  menuDisplayName?: string | null;
  menuDisplayImage?: string | null;
  specialType?: string | null;
  endStamp?: string | null;
  parentId?: string | null;
};

type SpecialOfferCardProps = {
  special: SpecialOfferCardSpecial;
  href: string;
  inGridLayout?: boolean;
  showDaysLeft?: boolean;
  role?: string;
  'aria-label'?: string;
  'aria-roledescription'?: string;
};

const ENDING_SOON_THRESHOLD_DAYS = 7;
const MAX_DISPLAY_DAYS = 14;
const GRID_DESKTOP_BREAKPOINT = '600px';

function getDaysLeft(endStamp?: string | null): number | null {
  if (!endStamp) {
    return null;
  }

  const end = new Date(endStamp).getTime();
  const now = Date.now();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

  return Math.max(0, diff);
}

function formatDaysLeft(daysLeft: number): string {
  if (daysLeft > MAX_DISPLAY_DAYS) {
    return `${MAX_DISPLAY_DAYS}+ days left`;
  }

  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
}

function getProgressDescription(
  thresholdType: GamificationThresholdType | undefined,
  threshold: number,
  currentAmount: number
): string | null {
  const remaining = threshold - currentAmount;
  if (remaining <= 0) {
    return null;
  }

  if (thresholdType === 'NUMBER_OF_ITEMS') {
    return `Add ${remaining} more eligible ${pluralize('item', remaining)} to complete the offer`;
  }

  if (thresholdType === 'SPENDING_AMOUNT') {
    return `Add ${formatCurrency(remaining, { trimZeroCents: true })} more of eligible items to complete the offer`;
  }

  if (thresholdType === 'PRODUCT_GRAMS') {
    return `Add ${remaining}g more of eligible items to complete the offer`;
  }

  return null;
}

export const SpecialOfferCard = observer(
  ({
    special,
    href,
    inGridLayout = false,
    showDaysLeft = true,
    role,
    'aria-label': ariaLabel,
    'aria-roledescription': ariaRoleDescription,
  }: SpecialOfferCardProps): JSX.Element => {
    const { menuDisplayName, menuDisplayImage, parentId } = special;
    const { discountGamificationDetails } = useCart();
    const flags = useFlags();

    const multiTierDiscountsEnabled = flags['core.cats-pos.multi-tier-discounts.rollout'] ?? false;
    const hasParent = multiTierDiscountsEnabled && parentId != null;

    const daysLeft = showDaysLeft ? getDaysLeft(special.endStamp) : null;
    const isEndingSoon = daysLeft !== null && daysLeft <= ENDING_SOON_THRESHOLD_DAYS;

    const matchingGamification = discountGamificationDetails?.find((offer) => offer.specialId === special._id);
    const activeThreshold = matchingGamification?.thresholds[0];
    const { currentAmount = 0, threshold = 0, thresholdType } = activeThreshold ?? {};
    const progress = threshold > 0 ? Math.min(100, Math.max(0, (currentAmount / threshold) * 100)) : 0;
    const showProgress = Boolean(matchingGamification) && currentAmount > 0 && Number.isFinite(progress);
    const progressDescription = showProgress ? getProgressDescription(thresholdType, threshold, currentAmount) : null;

    const imageSrc = replaceAWSSourceWithImgix(menuDisplayImage) || DEFAULT_SPECIALS_CARD_IMG;

    return (
      <CardLink
        href={href}
        $inGridLayout={inGridLayout}
        data-testid='special-offer-card'
        role={role}
        aria-label={ariaLabel}
        aria-roledescription={ariaRoleDescription}
      >
        {hasParent && <MultiTierBadge>Buy More Save More</MultiTierBadge>}

        <ImageSection>
          <CardImage src={imageSrc} alt={menuDisplayName ?? 'Special offer'} loading='lazy' />
        </ImageSection>

        <InfoSection>
          <TitleSection>
            <TextContent>
              <Title $inGridLayout={inGridLayout}>{menuDisplayName}</Title>
              {daysLeft !== null && (
                <CountdownRow>
                  <CountdownText>{formatDaysLeft(daysLeft)}</CountdownText>
                  {!inGridLayout && isEndingSoon && <EndingSoonBadge>Ending soon!</EndingSoonBadge>}
                </CountdownRow>
              )}
            </TextContent>

            {!inGridLayout && showProgress && (
              <ProgressWrapper>
                <ProgressCircle progress={progress} size={32} strokeWidth={3}>
                  <DiscountTag width={12} height={12} />
                </ProgressCircle>
              </ProgressWrapper>
            )}
          </TitleSection>

          {inGridLayout && showProgress && progressDescription && (
            <ProgressRow>
              <ProgressWrapper>
                <ProgressCircle progress={progress} size={32} strokeWidth={3}>
                  <DiscountTag width={12} height={12} />
                </ProgressCircle>
              </ProgressWrapper>
              <ProgressDescription>{progressDescription}</ProgressDescription>
            </ProgressRow>
          )}
        </InfoSection>
      </CardLink>
    );
  }
);

SpecialOfferCard.displayName = 'SpecialOfferCard';

const CardLink = styled(Link)<{ $inGridLayout: boolean }>`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: 12px;
  color: inherit;
  padding: 16px;
  position: relative;
  text-decoration: none;

  ${({ $inGridLayout }) =>
    $inGridLayout
      ? css`
          display: grid;
          gap: 12px;
          grid-template-columns: 0.75fr 1fr;
          min-width: 0;
          width: 100%;

          @media (min-width: ${GRID_DESKTOP_BREAKPOINT}) {
            gap: 20px;
            grid-template-columns: 1fr 1fr;
          }
        `
      : css`
          align-items: center;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: var(--dcm-special-card-min-height, 234px);
          max-width: 358px;
          min-width: 358px;
        `}
`;

// Same pill styling as special-card's "Members Only" badge, reused here for
// visual consistency across the specials feature's card components. Every
// themeable value is exposed as a --dcm-* custom property (see
// src/theme/core-menu-variables.ts) so remote Ecommerce Pro SDK themes can
// restyle or reposition the badge without overriding the component outright.
const MultiTierBadge = styled.span`
  background: var(--dcm-specials-multi-tier-badge-bg, #00624a);
  border-radius: var(--dcm-radius-sm, 4px);
  color: var(--dcm-specials-multi-tier-badge-text-color, #fff);
  font-size: var(--dcm-specials-multi-tier-badge-font-size, 11px);
  font-weight: var(--dcm-font-weight-semibold, 600);
  line-height: 1;
  padding: var(--dcm-specials-multi-tier-badge-padding, 4px 8px);
  position: absolute;
  right: var(--dcm-specials-offer-card-multi-tier-badge-offset-right, 12px);
  text-transform: var(--dct--customized-textTransforms-tags, uppercase);
  top: var(--dcm-specials-offer-card-multi-tier-badge-offset-top, 12px);
  white-space: nowrap;
  z-index: var(--dcm-specials-multi-tier-badge-z-index, 1);
`;

const ImageSection = styled.div`
  background-color: var(--dcm-special-card-image-bg, #f4f5f5);
  border-radius: 12px;
  aspect-ratio: var(--dcm-special-card-image-ratio, 4 / 3);
  overflow: hidden;
  width: 100%;
`;

const CardImage = styled.img`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  min-width: 0;
  width: 100%;
`;

const TitleSection = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
  width: 100%;
`;

const TextContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.p<{ $inGridLayout: boolean }>`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--dcm-special-card-title-lines, 3);
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  display: -webkit-box;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  overflow: hidden;

  ${({ $inGridLayout }) =>
    $inGridLayout &&
    css`
      @media (min-width: ${GRID_DESKTOP_BREAKPOINT}) {
        font-size: 16px;
      }
    `}
`;

const CountdownRow = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 8px;
  letter-spacing: 0.06px;
`;

const CountdownText = styled.span`
  color: #485055;
  white-space: nowrap;
`;

const EndingSoonBadge = styled.span`
  color: #f35000;
  font-weight: 500;
`;

const ProgressWrapper = styled.div`
  flex-shrink: 0;
  height: 32px;
  width: 32px;
`;

const ProgressRow = styled.div`
  align-items: center;
  display: none;
  gap: 8px;

  @media (min-width: ${GRID_DESKTOP_BREAKPOINT}) {
    display: flex;
  }
`;

const ProgressDescription = styled.p`
  color: #00624a;
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06px;
  line-height: 16px;
  min-width: 0;
`;
